import React, { useState, useEffect } from 'react';
import { Tag, Plus, Edit2, X, Check, SlidersHorizontal } from 'lucide-react';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import CTable from '../components/shared/c-table';
import LeaveApprovalChain from '../components/LeaveApprovalChain';
import { leaveStepLabel } from '../utils/leaveApproval';

const defaultForm = {
  name: '',
  is_balance_tracked: true,
  default_quota: '',
  enforce_quota: false,
  allow_backdate_days: 0,
  min_notice_days: 0,
  backdated_auto_approve: false,
  max_consecutive_days: '',
  is_active: true,
};

// The editor's shape for a saved chain: the server's step carries labels too, which the
// editor neither shows nor sends back.
const toEditorSteps = (steps = []) =>
  steps.map((step) => ({
    mode: step.mode,
    approvers: (step.approvers || []).map(({ approver_kind, role_id, user_id }) => ({
      approver_kind,
      role_id: role_id ?? null,
      user_id: user_id ?? null,
    })),
  }));

// Blank means "no rule", which the API stores as null. Number() would turn it into 0,
// and 0 days is a real (and very different) setting.
const optionalNumber = (value) => (value === '' || value === null ? null : Number(value));

const LeaveTypes = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [chain, setChain] = useState([]);
  const [approverOptions, setApproverOptions] = useState({ roles: [], users: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!hasPermission('leaves', 'manage_types')) {
      toast.error('Access denied');
      navigate('/leaves');
      return;
    }
    fetchTypes();
    api.get('/api/leaves/types/approvers')
      .then(res => setApproverOptions(res.data.data || { roles: [], users: [] }))
      .catch(() => toast.error('Failed to load approver options'));
  }, [hasPermission, navigate]);

  const fetchTypes = () => {
    setLoading(true);
    api.get('/api/leaves/types', { params: { include_inactive: true } })
      .then(res => setTypes(res.data.data || []))
      .catch(() => toast.error('Failed to load leave types'))
      .finally(() => setLoading(false));
  };

  const openCreate = () => {
    setEditId(null);
    setForm(defaultForm);
    setChain([]);
    setShowForm(true);
  };

  const openEdit = (t) => {
    setEditId(t.id);
    setForm({
      name: t.name,
      is_balance_tracked: t.is_balance_tracked,
      default_quota: t.default_quota ?? '',
      enforce_quota: t.enforce_quota,
      allow_backdate_days: t.allow_backdate_days,
      min_notice_days: t.min_notice_days ?? 0,
      backdated_auto_approve: t.backdated_auto_approve ?? false,
      max_consecutive_days: t.max_consecutive_days ?? '',
      is_active: t.is_active,
    });
    setChain(toEditorSteps(t.approval_steps));
    setShowForm(true);
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async e => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    const incomplete = chain.some(step => step.approvers.some(approver =>
      (approver.approver_kind === 'role' && !approver.role_id) || (approver.approver_kind === 'user' && !approver.user_id)
    ));
    if (incomplete) { toast.error('Every role or person approver needs someone chosen'); return; }
    setSaving(true);
    const payload = {
      ...form,
      default_quota: optionalNumber(form.default_quota),
      allow_backdate_days: Number(form.allow_backdate_days) || 0,
      min_notice_days: Number(form.min_notice_days) || 0,
      max_consecutive_days: optionalNumber(form.max_consecutive_days),
      approval_steps: chain,
    };
    try {
      if (editId) {
        await api.put(`/api/leaves/types/${editId}`, payload);
        toast.success('Leave type updated');
      } else {
        await api.post('/api/leaves/types', payload);
        toast.success('Leave type created');
      }
      setShowForm(false);
      fetchTypes();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save leave type');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { key: 'name', header: 'Name', cellClassName: 'font-medium whitespace-normal min-w-[8rem]' },
    {
      key: 'is_balance_tracked',
      header: 'Tracked',
      render: (t) => (t.is_balance_tracked ? (
        <span className="inline-flex items-center gap-1 text-success-700">
          <Check className="h-3.5 w-3.5" /> Yes
        </span>
      ) : (
        <span className="text-gray-400">No</span>
      )),
    },
    {
      key: 'default_quota',
      header: 'Default Quota',
      cellClassName: 'text-gray-700',
      render: (t) => t.default_quota ?? '-',
    },
    { key: 'allow_backdate_days', header: 'Backdate Days', cellClassName: 'text-gray-700' },
    {
      key: 'approval',
      header: 'Approval',
      // The one column allowed to wrap: a chain summary is the longest text in the
      // table, and unwrapped it pushed the table past the card at desktop width.
      cellClassName: 'text-gray-700 whitespace-normal min-w-[12rem] max-w-sm',
      render: (t) => {
        const steps = t.approval_steps || [];
        if (!steps.length) return <span className="text-gray-400">Anyone who can approve</span>;
        const summary = steps.map(leaveStepLabel).join(' → ');
        return (
          <span data-testid={`leave-types-chain-${t.id}`} title={summary}>
            {summary}
          </span>
        );
      },
    },
    {
      key: 'is_active',
      header: 'Status',
      render: (t) => (
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${t.is_active ? 'bg-success-100 text-success-800' : 'bg-gray-100 text-gray-600'}`}>
          {t.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (t) => (
        <button
          type="button"
          onClick={() => openEdit(t)}
          className="text-primary-600 hover:text-primary-800 font-medium inline-flex items-center gap-1"
        >
          <Edit2 className="h-3.5 w-3.5" />
          Edit
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Tag className="h-7 w-7 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Leave Types</h1>
        </div>
        <button
          data-testid="leave-types-add-btn"
          onClick={openCreate}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Type
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-soft border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900">{editId ? 'Edit Leave Type' : 'New Leave Type'}</h2>
            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-danger-600">*</span>
                </label>
                <input
                  name="name"
                  data-testid="leave-types-field-name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default Quota (days)</label>
                <input
                  type="number"
                  name="default_quota"
                  value={form.default_quota}
                  onChange={handleChange}
                  min={0}
                  placeholder="Leave blank if not tracked"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Rules */}
            <div data-testid="leave-types-rules" className="border-t border-gray-200 pt-4 space-y-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 flex items-center">
                  <SlidersHorizontal className="h-4 w-4 mr-2 text-primary-600" />
                  Rules
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Checked when someone files a request of this type.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notice required (days)</label>
                  <input
                    type="number"
                    name="min_notice_days"
                    data-testid="leave-types-field-notice"
                    value={form.min_notice_days}
                    onChange={handleChange}
                    min={0}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">0 allows a same-day request.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Allow Backdate Days</label>
                  <input
                    type="number"
                    name="allow_backdate_days"
                    value={form.allow_backdate_days}
                    onChange={handleChange}
                    min={0}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">How far into the past a start date may sit.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Longest request (business days)</label>
                  <input
                    type="number"
                    name="max_consecutive_days"
                    data-testid="leave-types-field-max-days"
                    value={form.max_consecutive_days}
                    onChange={handleChange}
                    min={1}
                    placeholder="Leave blank for no limit"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  name="backdated_auto_approve"
                  data-testid="leave-types-field-backdated-auto"
                  checked={form.backdated_auto_approve}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600"
                />
                Approve backdated requests automatically
              </label>
            </div>

            {/* Approval chain */}
            <div className="border-t border-gray-200 pt-4">
              <LeaveApprovalChain
                steps={chain}
                onChange={setChain}
                roles={approverOptions.roles}
                users={approverOptions.users}
              />
            </div>

            <div className="flex flex-wrap gap-6 border-t border-gray-200 pt-4">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_balance_tracked"
                  checked={form.is_balance_tracked}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600"
                />
                Track balance
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  name="enforce_quota"
                  checked={form.enforce_quota}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600"
                />
                Enforce quota (future)
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={form.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600"
                />
                Active
              </label>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="leave-types-submit"
                disabled={saving}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-60"
              >
                <Check className="h-4 w-4 mr-2" />
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      <CTable
        columns={columns}
        data={types}
        loading={loading}
        rowKey="id"
        getRowProps={(t) => ({ 'data-testid': `leave-types-row-${t.id}` })}
        emptyMessage={
          <>
            <Tag className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm">No leave types configured</p>
          </>
        }
      />
    </div>
  );
};

export default LeaveTypes;
